import { useNavigate, useRouterState } from '@tanstack/react-router';
import { type ClipboardEvent, useCallback, useEffect, useState } from 'react';
import type { Ricerca } from './api/anagrafiche.js';
import { RICERCA_INIZIALE } from './api/anagrafiche.js';
import { type RicercaUrl, aUrl, daUrl } from './ricerca-url.js';

/**
 * Lo stato di un elenco, tenuto nell'indirizzo del browser.
 *
 * Vale per qualunque elenco — anagrafiche, ordini, articoli, listini — perché
 * la forma della ricerca è sempre la stessa: pagina, ordinamento, testo
 * cercato, filtri.
 *
 * Il testo digitato è l'unico pezzo che vive anche fuori dall'indirizzo: si
 * scrive lettera per lettera e l'indirizzo si aggiorna dopo una pausa, senza
 * riempire la cronologia di un passo per ogni tasto.
 */
export function useRicerca(iniziale: Ricerca = RICERCA_INIZIALE) {
  const navigate = useNavigate();
  const search = useRouterState({ select: (s) => s.location.search as RicercaUrl });
  const ricerca = daUrl(search, iniziale);

  const [testo, setTesto] = useState(ricerca.q);

  // se l'indirizzo cambia da fuori — tasto indietro, link incollato — il
  // campo di ricerca deve seguirlo
  useEffect(() => setTesto(ricerca.q), [ricerca.q]);

  const scrivi = useCallback(
    (nuova: Ricerca, sostituisci = false) => {
      void navigate({ search: aUrl(nuova, iniziale) as never, replace: sostituisci });
    },
    [navigate, iniziale],
  );

  const cambia = useCallback(
    (modifica: Partial<Ricerca>) => scrivi({ ...ricerca, page: 1, ...modifica }),
    [ricerca, scrivi],
  );

  // la digitazione aggiorna l'indirizzo dopo una breve pausa, e lo sostituisce
  // invece di impilarlo: il tasto indietro deve tornare alla ricerca
  // precedente, non alla lettera precedente
  useEffect(() => {
    if (testo === ricerca.q) return;
    const attesa = setTimeout(() => scrivi({ ...ricerca, q: testo, page: 1 }, true), 250);
    return () => clearTimeout(attesa);
  }, [testo, ricerca, scrivi]);

  return {
    ricerca,
    testo,
    setTesto,
    /**
     * Da mettere sull'`onPaste` della casella: una colonna copiata da Excel
     * arriva con un PO per riga, e nella casella, che è di una riga sola, gli
     * a capo diventano virgole. Il servizio la legge come elenco di PO.
     */
    incolla: (e: ClipboardEvent<HTMLInputElement>) => {
      const incollato = e.clipboardData.getData('text');
      if (!/[\r\n\t]/.test(incollato)) return;
      e.preventDefault();
      const campo = e.currentTarget;
      const pezzi = incollato.split(/[\r\n\t]+/).map((p) => p.trim()).filter(Boolean);
      setTesto(
        campo.value.slice(0, campo.selectionStart ?? campo.value.length) +
          pezzi.join(', ') +
          campo.value.slice(campo.selectionEnd ?? campo.value.length),
      );
    },
    cambia,
    /** Va a una pagina senza toccare il resto. */
    vaiA: (page: number) => scrivi({ ...ricerca, page }),
    /** Ordina per una colonna, invertendo il verso se è già quella. */
    ordinaPer: (chiave: string) =>
      cambia({
        sort: chiave,
        dir: ricerca.sort === chiave && ricerca.dir === 'asc' ? 'desc' : 'asc',
      }),
    /** Imposta o toglie un filtro. */
    filtra: (chiave: string, valore: string | number | undefined) =>
      cambia({ filters: { ...ricerca.filters, [chiave]: valore } }),
    /** Applica una vista salvata, sostituendo tutto. */
    applica: (definizione: Record<string, unknown>) =>
      scrivi({ ...iniziale, ...(definizione as Partial<Ricerca>) } as Ricerca),
  };
}
